import { iSettings, iWord, iWords, iWordsHistory } from "@/lib/types";
import { words } from "@/lib/words";
import { useEffect, useState } from "react";
import { useHistory } from "./useHistory";
import { GLOBAL_SETTINGS } from "@/lib/settings";
import {
  categorizePercentage,
  filterWords,
  getChoices,
  getHistoryCategorizedSummary,
  getRandomWordFromPool,
} from "./helper";

export const useQuiz = () => {
  const { setSettings, settings, wordHistory, setWordHistory } = useHistory();

  const [wordPool, setWordPool] = useState<iWords>();
  const [isQuizStarted, setStartQuiz] = useState<boolean>(false);
  const [showReview, setShowReview] = useState<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState<iWord>();
  const [choice, setChoice] = useState<string | undefined>();
  const [choices, setChoices] = useState<string[]>([]);
  const [questionsAttempted, setQuestionsAttempted] = useState<string[]>([]);

  const onQuizEnd = () => {
    setStartQuiz(false);
    setShowReview(true);
  };

  const startQuizWithSettings = (settings: iSettings) => {
    if (settings) {
      const allWords: iWordsHistory = Object.keys(words).reduce((acc, key) => {
        acc[key] = {
          ...(words[key] as iWord),
          history: wordHistory[key],
        };
        return acc;
      }, {} as iWordsHistory);
      const filteredWords = filterWords(allWords, settings);
      setWordPool(filteredWords);
      setStartQuiz(true);
    }
  };

  const onClick = (selectedAnswer: string) => {
    if (choice) return;
    setQuestionsAttempted([...questionsAttempted, String(currentQuestion!.id)]);
    setChoice(selectedAnswer);

    if (!currentQuestion) return;

    if (currentQuestion.id in wordHistory) {
      const attempts = wordHistory[currentQuestion!.id].attempts + 1;
      const correct =
        wordHistory[currentQuestion!.id].correct +
        (selectedAnswer === currentQuestion.english ? 1 : 0);
      const percentage = (correct / attempts) * 100;

      setWordHistory({
        ...wordHistory,
        [currentQuestion!.id]: {
          attempts,
          correct,
          percentage,
          category: categorizePercentage(percentage),
        },
      });
    } else {
      const attempts = 1;
      const correct = selectedAnswer === currentQuestion.english ? 1 : 0;
      const percentage = (correct / attempts) * 100;

      setWordHistory({
        ...wordHistory,
        [currentQuestion!.id]: {
          attempts,
          correct,
          percentage,
          category: categorizePercentage(percentage),
        },
      });
    }

    const timer = setTimeout(() => {
      const nextWord = getRandomWordFromPool(
        wordPool,
        String(currentQuestion?.id),
      );

      setCurrentQuestion(nextWord);
      setChoice(undefined);
      setChoices(getChoices(nextWord));
    }, GLOBAL_SETTINGS.delayBeforeNextQuestion);

    return () => clearTimeout(timer);
  };

  useEffect(() => {
    if (isQuizStarted) {
      const word = getRandomWordFromPool(wordPool);
      setCurrentQuestion(word);
      setChoices(getChoices(word));
    }
  }, [isQuizStarted]);

  const onReviewEnd = () => {
    setShowReview(false);
    setStartQuiz(false);
    setQuestionsAttempted([]);
    setChoice(undefined);
    setChoices([]);
    setCurrentQuestion(undefined);
    setWordPool(undefined);
  };

  return {
    startQuizWithSettings,
    isQuizStarted,
    setStartQuiz,
    getRandomWordFromPool,
    currentQuestion,
    setCurrentQuestion,
    getChoices,
    choice,
    onClick,
    choices,
    settings,
    setSettings,
    summary: getHistoryCategorizedSummary(wordHistory),
    onQuizEnd,
    showReview,
    questionsAttempted,
    onReviewEnd,
    wordHistory,
    wordPool,
  };
};
